import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Input, DatePicker, Select, Button, Table, Modal, Form, InputNumber, message, Radio, Row, Col } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import OrderManagementPageModel from '../pageModels/OrderManagementPageModel';
import { Order, OrderStatus, CommissionAllocationItem } from '../../models/Order';
import '../styles/OrderManagement.css';
import { globalStore } from '../../models/globalStore';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const OrderManagement: React.FC = () => {
    const [model] = useState(() => new OrderManagementPageModel());
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [refundModalVisible, setRefundModalVisible] = useState(false);
    const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
    const [refundForm] = Form.useForm();
    const [feeRateModalVisible, setFeeRateModalVisible] = useState(false);
    const [confirmFeeRateModalVisible, setConfirmFeeRateModalVisible] = useState(false);
    const [feeRateForm] = Form.useForm();
    const [newFeeRate, setNewFeeRate] = useState<number>(0);

    useEffect(() => {
        globalStore.selectedMenuKey = 'order-management';
        model.fetchOrders();
    }, [model]);

    const handleSearch = () => {
        model.fetchOrders();
    };

    const handleReset = () => {
        model.resetSearchParams();
        model.fetchOrders();
    };

    const showOrderDetail = (order: Order) => {
        model.setSelectedOrder(order);
        setDetailModalVisible(true);
    };

    const handleRefund = (type: 'full' | 'partial') => {
        setRefundType(type);
        setRefundModalVisible(true);
    };

    const handleRefundSubmit = async () => {
        try {
            const values = await refundForm.validateFields();
            const success = refundType === 'full'
                ? await model.cancelAndRefund(model.selectedOrder!.orderId, values.reason)
                : await model.partialRefund(model.selectedOrder!.orderId, values.reason, values.amount);

            if (success) {
                message.success('退款操作成功');
                setRefundModalVisible(false);
                model.fetchOrders();
            } else {
                message.error('退款操作失败');
            }
        } catch (error) {
            console.error('退款表单验证失败:', error);
        }
    };

    const handleCopyOrderId = (orderId: string) => {
        navigator.clipboard.writeText(orderId).then(() => {
            message.success('订单ID已复制到剪贴板');
        }).catch(() => {
            message.error('复制失败');
        });
    };

    const handleModifyFeeRate = () => {
        setFeeRateModalVisible(true);
        feeRateForm.setFieldsValue({ feeRate: model.selectedOrder?.feeRate || 0 });
    };

    const handleFeeRateSubmit = async () => {
        try {
            const values = await feeRateForm.validateFields();
            setNewFeeRate(values.feeRate);
            setConfirmFeeRateModalVisible(true);
        } catch (error) {
            console.error('费率表单验证失败:', error);
        }
    };

    const handleConfirmFeeRateChange = async () => {
        try {
            const success = await model.modifyOrderFeeRate(model.selectedOrder!.orderId, newFeeRate);
            if (success) {
                message.success('费率修改成功');
                setConfirmFeeRateModalVisible(false);
                setFeeRateModalVisible(false);
                feeRateForm.resetFields();
            } else {
                message.error('费率修改失败');
            }
        } catch (error) {
            console.error('费率修改失败:', error);
            message.error('费率修改失败');
        }
    };

    const columns = [
        {
            title: '订单ID',
            dataIndex: 'orderId',
            key: 'orderId',
            width: 200,
            render: (orderId: string) => (
                <span>
                    {orderId}
                    <CopyOutlined className="copy-icon" onClick={(e) => {
                        e.stopPropagation();
                        handleCopyOrderId(orderId);
                    }} />
                </span>
            ),
        },
        {
            title: '商品名称',
            dataIndex: 'productName',
            key: 'productName',
            width: 200,
            render: (productName: string, record: Order) => (
                <span className="product-name-cell">
                    {record.selfOperated && (
                        <span className="self-operated-tag">自营</span>
                    )}
                    {productName}
                </span>
            ),
        },
        {
            title: '买家昵称',
            dataIndex: 'buyerNickname',
            key: 'buyerNickname',
            width: 120,
        },
        {
            title: '卖家昵称',
            dataIndex: 'sellerNickname',
            key: 'sellerNickname',
            width: 120,
        },
        {
            title: '订单金额',
            dataIndex: 'orderAmount',
            key: 'orderAmount',
            width: 100,
            render: (amount: number) => `¥${amount.toFixed(2)}`,
        },
        {
            title: '订单状态',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: OrderStatus) => (
                <span className={`status-tag status-${OrderStatus[status].toLowerCase()}`}>
                    {model.getStatusText(status)}
                </span>
            ),
        },
        {
            title: '费率',
            dataIndex: 'feeRate',
            key: 'feeRate',
            width: 100,
            render: (feeRate: number) => `${feeRate}%`,
        },
        {
            title: '下单时间',
            dataIndex: 'orderTime',
            key: 'orderTime',
            width: 180,
            render: (time: string) => model.formatTime(time),
        },
    ];

    return (
        <div className="order-management">
            <div className="filter-section">
                <div className="filter-row" >
                    <div className="filter-item"> 
                        <Input
                            style={{ width: 200 }}
                            placeholder="任意买家手机号/ID"
                            onChange={e => {
                                const value = e.target.value;
                                model.setSearchParams({
                                    buyerPhone: value,
                                    buyerId: value
                                });
                            }}
                        />
                    </div>
                    <div className="filter-item"> 
                        <Input
                            style={{ width: 200 }}
                            placeholder="任意卖家手机号/ID"
                            onChange={e => {
                                const value = e.target.value;
                                model.setSearchParams({
                                    sellerPhone: value,
                                    sellerId: value
                                });
                            }}
                        />
                    </div>
                    
                    <div className="filter-item"> 
                        <Input
                            style={{ width: 200 }}
                            placeholder="商品ID/名称"
                            onChange={e => {
                                const value = e.target.value;
                                model.setSearchParams({
                                    productId: value,
                                    productName: value
                                });
                            }}
                        />
                    </div>
                    <div className="filter-item"> 
                        <RangePicker
                            placeholder={['下单开始时间', '下单结束时间']}
                            onChange={(dates) => {
                                if (dates) {
                                    model.setSearchParams({
                                        orderTimeStart: dates[0]?.toISOString(),
                                        orderTimeEnd: dates[1]?.toISOString()
                                    });
                                }
                            }}
                        />
                    </div>
                    <div className="filter-item"> 
                        <RangePicker
                            placeholder={['支付开始时间', '支付结束时间']}
                            onChange={(dates) => {
                                if (dates) {
                                    model.setSearchParams({
                                        paymentTimeStart: dates[0]?.toISOString(),
                                        paymentTimeEnd: dates[1]?.toISOString()
                                    });
                                }
                            }}
                        />
                    </div> 
                </div> 
                <div className="filter-row status-row">
                    <div className="filter-item">
                        <span className="filter-label">订单状态:</span>
                        <Radio.Group
                            onChange={(e) => model.setSearchParams({ status: e.target.value })}
                            value={model.searchParams.status ?? null}
                        >
                            <Radio.Button value={null}>全部</Radio.Button>
                            <Radio.Button value={OrderStatus.PENDING_PAYMENT}>待支付</Radio.Button>
                            <Radio.Button value={OrderStatus.PAID_PENDING_SERVICE}>待开启服务</Radio.Button>
                            <Radio.Button value={OrderStatus.SERVICE_IN_PROGRESS}>服务进行中</Radio.Button>
                            <Radio.Button value={OrderStatus.PENDING_ACCEPTANCE}>待验收</Radio.Button>
                            <Radio.Button value={OrderStatus.COMPLETED}>已完成</Radio.Button>
                            <Radio.Button value={OrderStatus.CANCELLED}>已取消</Radio.Button>
                        </Radio.Group>
                    </div>
                    <div className="filter-buttons" style={{marginRight: '22px'}}>
                        <Button type="primary" style={{marginRight: '20px'}} onClick={handleSearch}>查询</Button>
                        <Button onClick={handleReset}>重置</Button>
                    </div>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={model.orders}
                rowKey="orderId"
                loading={model.loading}
                onRow={(record) => ({
                    onClick: () => showOrderDetail(record),
                })}
                pagination={false}
            />

            <Modal
                title={
                    <span className="modal-title-content">
                        订单详情
                        {model.selectedOrder?.selfOperated && (
                            <span className="self-operated-tag-title">自营</span>
                        )}
                    </span>
                }
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setDetailModalVisible(false)}>关闭</Button>,
                    <Button
                        key="modifyFeeRate"
                        type="primary"
                        onClick={handleModifyFeeRate}
                    >
                        修改费率
                    </Button>,
                    <Button
                        key="refund"
                        type="primary"
                        danger
                        onClick={() => handleRefund('full')}
                        disabled={model.selectedOrder?.status === OrderStatus.CANCELLED}
                    >
                        取消并退款
                    </Button>,
                    <Button
                        key="partialRefund"
                        type="primary"
                        onClick={() => handleRefund('partial')}
                        disabled={model.selectedOrder?.status === OrderStatus.CANCELLED}
                    >
                        部分退款
                    </Button>,
                ]}
                width={800}
            >
                {model.selectedOrder && (
                    <>
                        <Row>
                            <Col span={12}>
                                <div className="detail-row">
                                    <span className="detail-label">订单ID:</span>
                                    <span className="detail-value">{model.selectedOrder.orderId}</span>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div className="detail-row">
                                    <span className="detail-label">商品信息:</span>
                                    <span className="detail-value">
                                        {model.selectedOrder.productName} ({model.getVersionText(model.selectedOrder.productVersion)})
                                    </span>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12}>
                                <div className="detail-row">
                                    <span className="detail-label">订单金额:</span>
                                    <span className="detail-value">¥{model.selectedOrder.orderAmount.toFixed(2)}</span>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div className="detail-row">
                                    <span className="detail-label">订单状态:</span>
                                    <span className="detail-value">{model.getStatusText(model.selectedOrder.status)}</span>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12}>
                                <div className="detail-row">
                                    <span className="detail-label">费率:</span>
                                    <span className="detail-value">{model.selectedOrder.feeRate}%</span>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div className="detail-row">
                                    <span className="detail-label">完成时间:</span>
                                    <span className="detail-value">{model.formatTime(model.selectedOrder.completionTime)}</span>
                                </div>
                            </Col>
                        </Row>
                        <div className="detail-row">
                            <span className="detail-label">佣金分配方案:</span>
                        </div>
                        <table className="commission-table">
                            <thead>
                                <tr>
                                    <th>卖家昵称</th>
                                    <th>手机号</th>
                                    <th>佣金金额</th>
                                    <th>负责内容</th>
                                </tr>
                            </thead>
                            <tbody>
                                {model.selectedOrder.commissionAllocation.items.map((item: CommissionAllocationItem, index: number) => (
                                    <tr key={index}>
                                        <td>{item.sellerNickname}</td>
                                        <td>{item.sellerPhone}</td>
                                        <td>¥{item.commissionAmount.toFixed(2)}</td>
                                        <td>{item.responsibility}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </Modal>

            <Modal
                title={refundType === 'full' ? '取消订单并退款' : '部分退款'}
                open={refundModalVisible}
                onCancel={() => setRefundModalVisible(false)}
                okText="确定"
                cancelText="取消"
                onOk={handleRefundSubmit}
                width={400}
            >
                <Form form={refundForm} layout="vertical">
                    <Form.Item
                        name="reason"
                        label="退款原因"
                        rules={[{ required: true, message: '请输入退款原因' }]}
                    >
                        <TextArea rows={4} placeholder="请输入退款原因" />
                    </Form.Item>
                    {refundType === 'partial' && (
                        <Form.Item
                            name="amount"
                            label="退款金额"
                            rules={[
                                { required: true, message: '请输入退款金额' },
                                {
                                    type: 'number',
                                    min: 0.01,
                                    max: model.selectedOrder?.orderAmount || 0,
                                    message: '退款金额必须大于0且不能超过订单金额'
                                }
                            ]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder="请输入退款金额"
                                precision={2}
                                step={0.01}
                            />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal
                title="修改费率"
                open={feeRateModalVisible}
                onCancel={() => setFeeRateModalVisible(false)}
                okText="确定"
                cancelText="取消"
                onOk={handleFeeRateSubmit}
                width={400}
            >
                <Form form={feeRateForm} layout="vertical">
                    <Form.Item
                        name="feeRate"
                        label="费率"
                        rules={[
                            { required: true, message: '请输入费率' },
                            { type: 'number', min: 0, max: 100, message: '费率必须在0-100之间' }
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            placeholder="请输入费率"
                            precision={2}
                            step={0.01}
                            addonAfter="%"
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="确认修改费率"
                open={confirmFeeRateModalVisible}
                onCancel={() => setConfirmFeeRateModalVisible(false)}
                okText="确认"
                cancelText="取消"
                onOk={handleConfirmFeeRateChange}
                width={400}
            >
                <p>确认修改该订单费率从{model.selectedOrder?.feeRate}%改为{newFeeRate}%吗？</p>
            </Modal>
        </div>
    );
};

export default observer(OrderManagement); 